import { ReactElement } from "react";
import { MdInfo } from "react-icons/md";
import { IButton } from "../Button/Button";
import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { ICON_SIZES } from "../../helpers/constants";
import useMobile from "src/hooks/useMobile";

interface IInfoBlock {
  icon?: ReactElement | null;
  message?: String | ReactElement;
  buttons?: Array<IButton>;
  bg?: String;
  title?: String;
  displayMobileIcon?: boolean;
  mobileButtonsPosition?: "right" | "bottom";
  desktopButtonsPosition?: "right" | "bottom";
}

const InfoBlock: React.FC<IInfoBlock> = ({
  icon = <MdInfo size={ICON_SIZES.MD} />,
  title,
  message,
  buttons = null,
  displayMobileIcon = false,
  bg = "bg-sky-300/50 dark:bg-gray-900/80",
  mobileButtonsPosition = "right",
  desktopButtonsPosition = "right",
}) => {
  const { isMobile } = useMobile();

  return (
    <div className={clsx(["flex", "items-center", "justify-center", "w-full"])}>
      <div
        className={clsx([
          "w-full",
          "flex",
          "flex-row",
          "md:flex-row",
          "rounded-md",
          "md:rounded-lg",
          "items-center",
          "gap-2",
          "p-2",
          "md:p-2",
          bg,
          "relative",
          "shadow-sm",
          "border-0 border-dashed border-gray-400 dark:border-0",
        ])}
      >
        {icon && (
          <div
            className={clsx([
              "text-red-400 dark:text-cyan-400",
              {
                hidden: !displayMobileIcon,
              },
              "md:flex",
            ])}
          >
            {icon}
          </div>
        )}
        <div
          className={clsx([
            "flex",
            "items-center",
            "gap-3",
            "md:gap-3",
            {
              "flex-col": mobileButtonsPosition === "bottom",
              "flex-row": mobileButtonsPosition === "right",
              "lg:flex-row": desktopButtonsPosition === "right",
              "lg:flex-col": desktopButtonsPosition === "bottom",
              "md:gap-3": desktopButtonsPosition === "bottom",
              "md:gap-1": desktopButtonsPosition === "right",
            },
            "w-full",
            "justify-between",
            "z-0",
          ])}
        >
          <div
            className={clsx([
              "flex",
              "flex-col",
              "w-full",
              "flex-grow",
              "flex-shrink",
            ])}
          >
            {title && (
              <h5 className={clsx(["font-medium", "text-sm", "md:text-sm"])}>
                {title}
              </h5>
            )}
            <div
              className={clsx([
                "text-sm",
                "md:text-sm",
                "text-left",
                "flex",
                "w-full",
              ])}
            >
              {message}
            </div>
          </div>
          {buttons && (
            <div
              className={clsx([
                "flex",
                "flex-shrink",
                "justify-end",
                {
                  "w-full": mobileButtonsPosition === "bottom" && isMobile,
                  "w-auto": mobileButtonsPosition === "right",
                  "md:w-full": desktopButtonsPosition === "bottom",
                  "md:w-auto": mobileButtonsPosition === "right",
                },
              ])}
            >
              <ButtonGroup buttons={buttons} gap={1} align="right" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoBlock;
